import React from 'react';
import css from '@emotion/css';
import * as List from '../../../atoms/List';
import { PageInfo } from '../../../../libs/paging';
import { SortActionType } from '../../../../libs/filter';
import { formatIntNumber, formatDate, formatPercent } from '../../../../libs/format';
import { InstagramFeedPostSortField, ORDER } from '../../../../__generated__/globalTypes';
import CarouselModal from '../../../molecules/CarouselPopup';
import { SliderComponents, SliderCss, StyledComponents } from '../../../molecules/SliderTable';
import { usePostDetailModal } from '../../AnalyticsShared/BasePostList/usePostDetailModal';
import PostColumn from '../PostColumn';
import HashtagFeedPostDetail from '../HashtagFeedPostDetail';
import { getAllPostsHeaders } from '../postsHelpers';
import { AllInstagramHashtagFeedPosts_allInstagramHashtagFeedPosts } from './__generated__/AllInstagramHashtagFeedPosts';

export type InstagramFeedPostsSortAction = SortActionType<InstagramFeedPostSortField>;

interface InstagramHashtagFeedPostListProps extends InstagramFeedPostsSortAction {
  instagramFeedPosts: AllInstagramHashtagFeedPosts_allInstagramHashtagFeedPosts[];
  pageInfo: PageInfo;
}

const InstagramHashtagFeedPostList = (props: InstagramHashtagFeedPostListProps) => {
  const {
    sort: { field, order },
    setSort,
    instagramFeedPosts,
    pageInfo: { firstIndex, lastIndex, totalCount, currentPage, totalPages }
  } = props;

  const {
    openPostDetail,
    siblingPostId,
    handleClosePostDetail,
    handleClickPost,
    handleClickNext,
    handleClickPrev
  } = usePostDetailModal(instagramFeedPosts);

  return (
    <>
      <StyledComponents.ButtonWrapper css={SliderCss.boderTop}>
        <SliderComponents.PageCount total={totalCount} countLabel="Posts" />
      </StyledComponents.ButtonWrapper>
      <SliderComponents.Wrapper css={[SliderCss.marginTop, SliderCss.clearPadding]}>
        <SliderComponents.Container>
          <SliderComponents.SliderSection>
            <SliderComponents.SliderTable>
              <thead>
                <SliderComponents.HeaderRow css={SliderCss.bgndColor}>
                  <SliderComponents.StyckyHeaderDivWrapper>
                    <List.HeaderColumn
                      title="Post"
                      css={[SliderCss.clearPadding, SliderCss.width245, SliderCss.flexStart]}
                      isTable={false}
                    />
                  </SliderComponents.StyckyHeaderDivWrapper>
                  {/* slider */}
                  {getAllPostsHeaders(true, false).map(col => (
                    <List.HeaderColumn
                      key={col.columnName}
                      title={col.title}
                      help={col.help}
                      activeAsc={field === col.orderField && order === ORDER.ASC}
                      activeDesc={field === col.orderField && order === ORDER.DESC}
                      sortAsc={() => setSort({ field: col.orderField, order: ORDER.ASC })}
                      sortDesc={() => setSort({ field: col.orderField, order: ORDER.DESC })}
                      css={col.css}
                    />
                  ))}
                </SliderComponents.HeaderRow>
              </thead>
              <tbody>
                {instagramFeedPosts.map(item => {
                  const {
                    views,
                    engagement,
                    likes,
                    comments,
                    positiveRate,
                    negativeRate,
                    postDate,
                    id,
                    thumbNail,
                    content
                  } = item;

                  return (
                    <StyledComponents.StyledRowNew key={id} css={[SliderCss.paddingSides, SliderCss.rowHeight62]}>
                      <SliderComponents.StickyBodyRowWrapper css={SliderCss.clearPadding}>
                        <PostColumn
                          key={id}
                          id={id}
                          src={thumbNail}
                          title={content}
                          onClick={() => handleClickPost(id)}
                        />
                      </SliderComponents.StickyBodyRowWrapper>
                      {/* sliding section */}
                      <StyledComponents.NumberColumn data={formatIntNumber(views)} />
                      <StyledComponents.NumberColumn data={formatIntNumber(engagement)} />
                      <StyledComponents.NumberColumn data={formatIntNumber(likes)} />
                      <StyledComponents.NumberColumn data={formatIntNumber(comments)} />
                      <List.AmountColumn
                        data={formatPercent(positiveRate, false, 2)}
                        currency="%"
                        css={rateCurrencyFontSize}
                      />
                      <List.AmountColumn
                        data={formatPercent(negativeRate, false, 2)}
                        currency="%"
                        css={rateCurrencyFontSize}
                      />
                      <List.TextColumn data={formatDate(postDate)} />
                    </StyledComponents.StyledRowNew>
                  );
                })}
              </tbody>
            </SliderComponents.SliderTable>
          </SliderComponents.SliderSection>
        </SliderComponents.Container>
      </SliderComponents.Wrapper>
      <SliderComponents.Pager
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        first={firstIndex}
        last={lastIndex}
      />
      <CarouselModal
        open={!!openPostDetail}
        title=""
        cancel="OK"
        onClose={handleClosePostDetail}
        onClickNext={handleClickNext}
        onClickPrev={handleClickPrev}
        siblingPostId={siblingPostId}
      >
        <HashtagFeedPostDetail closeModal={handleClosePostDetail} />
      </CarouselModal>
    </>
  );
};

const rateCurrencyFontSize = css`
  div > p {
    font-size: 12px;
  }
`;

export default InstagramHashtagFeedPostList;
